<template>
  <div id="full-view" class="full-view">
    <div
      v-if="activePicture"
      class="full-view__cover"
      :class="{
        'full-view__cover--original': zoom === 1,
      }"
      @dblclick.prevent="changeView"
      v-dragscroll
    >
      <img :src="`${activePicture}`" alt="" />
    </div>

    <div class="full-view__info" v-if="estimation">
      <div class="full-view__aside">
        <div class="full-view__aside__head">
          <button
            type="button"
            class="button button--filter"
            @click="helpModalShow = true"
          >
            {{ $t('help') }} <i class="far fa-question-circle"></i>
          </button>
          <button type="button" class="button button--filter" @click="close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="full-view__aside__pictures">
          <button
            v-for="(image, i) in estimation.images"
            :key="i"
            class="full-view__aside__pictures__preview"
            @click="setActive(i)"
          >
            <img :src="`${image.smallUrl}`" alt="" />
          </button>
        </div>

        <div class="full-view__aside__expert">
          <p v-if="estimation.expert !== null">
            {{ $t('estimation.assigned_to') }}: {{ expertName(estimation.expert) }}
          </p>
          <button class="link" @click="expertModalShow = true">
            {{ $t('estimation.change_assigned_to') }}
          </button>
        </div>

        <div
          v-if="estimation.client !== null"
          class="full-view__aside__user"
          :class="{ 'full-view__aside__user__without-comment': !estimation.comment }"
        >
          <div>
            <p>{{ estimation.client.firstName }} {{ estimation.client.lastName }}</p>
            <p>{{ prettyDate(estimation.createdAt) }}</p>
          </div>
          <div>
            <p>{{ estimation.client.phoneNumber }}</p>
            <p>{{ estimation.client.email }}</p>
          </div>
        </div>

        <div v-if="estimation.comment" class="full-view__aside__comment">
          <p>{{ estimation.comment | truncate(150) }}</p>
          <button
            v-if="estimation.comment.length > 150"
            type="button"
            class="link"
            @click="commentModalShow = true"
          >
            {{ $t('show_more') }}
          </button>
        </div>
        <div v-if="estimation.expertComment" class="full-view__aside__comment">
          <p>
            {{ $t('estimation.expert_comment') }}
          </p>
          <p>
            {{ estimation.expertComment | truncate(50) }}
          </p>
          <button
            v-if="estimation.expertComment.length > 50"
            type="button"
            class="link"
            @click="commentExpertModalShow = true"
          >
            {{ $t('show_more') }}
          </button>
        </div>
        <div
          class="full-view__aside__actions"
          v-if="estimation.status === 'request'"
        >
          <div class="full-view__aside__actions__line">
            <textarea
              v-model="expertComment"
              class="input__message input__message--full-view"
              :placeholder="$t('estimation.add_an_expert_comment')"
            />
          </div>
          <div class="full-view__aside__actions__line">
            <div class="input__number">
              <input type="number" min="1" v-model="estimationValue" />
            </div>
            <button class="button button--valid" @click="estimationValidation">
              {{ $t('estimate') }}
            </button>
          </div>

          <div class="full-view__aside__actions__line">
            <button
              class="button button--danger"
              @click="rejectEstimation(rejectReason.unusable_photo)"
            >
              {{ $t('estimation.unusable_photo') }}
            </button>

            <ButtonDropdown :options="buttons" @action="rejectEstimation" />
          </div>
        </div>
        <div class="full-view__aside__actions" v-else>
          <p class="full-view__aside__actions__title">
            {{ $t('estimation.for') }} {{ toCurrency(estimation.estimationValue) }}
          </p>
          <p class="full-view__aside__actions__content">
            {{ $t('estimation.estimatedAt') }}
            {{ prettyFullDate(estimation.estimatedAt) }}
          </p>
        </div>
      </div>
    </div>

    <div class="full-view__nav">
      <div class="full-view__nav__container swiper" v-swiper:navSwiper="options">
        <div class="swiper-wrapper">
          <button
            v-for="(thumb, i) in estimations"
            :key="thumb.id"
            class="swiper-slide"
            :class="{ 'swiper-slide--active': thumb.id === id }"
            @click="getSelectedEstimation(i)"
          >
            <img :src="`${thumb.images[0].smallUrl}`" alt="" />
          </button>
        </div>

        <div class="swiper-button-prev" slot="button-prev">
          <i class="fas fa-chevron-left"></i>
        </div>
        <div class="swiper-button-next" slot="button-next" @click="nextSlide">
          <i class="fas fa-chevron-right"></i>
        </div>
      </div>
    </div>
    <ExpertListModal
      v-if="expertModalShow"
      :selected-expert="estimation.expert"
      @closeModal="expertModalShow = false"
      @updateAssignation="submitAssignation"
    />
    <HelpModal v-if="helpModalShow" @closeModal="helpModalShow = false" />
    <Modal
      v-if="commentModalShow"
      @closeModal="commentModalShow = false"
      title="estimation.comment"
      :content="estimation.comment ? estimation.comment : null"
    />
    <Modal
      v-if="commentExpertModalShow"
      @closeModal="commentExpertModalShow = false"
      title="estimation.expertComment"
      :content="estimation.expertComment ? estimation.expertComment : null"
    />
  </div>
</template>

<script>
import { findIndex } from 'lodash';
import { directive } from 'vue-awesome-swiper';
import { dragscroll } from 'vue-dragscroll';
import {
  formatDate,
  expertName,
  EstimationComponent,
  formatNumber,
} from '../../mixins';
import { ButtonDropdown } from '../../components/common';
import ExpertListModal from '../../components/ExpertListModal';
import HelpModal from '../../components/HelpModal';
import { mapActions, mapGetters } from 'vuex';
import { filter } from 'lodash';
import Modal from '../../components/Modal';

const slidesPerView = 5;

export default {
  components: { Modal, HelpModal, ButtonDropdown, ExpertListModal },
  mixins: [formatDate, expertName, EstimationComponent, formatNumber],

  directives: {
    swiper: directive,
    dragscroll,
  },

  data() {
    return {
      estimation: undefined,
      activePicture: undefined,
      estimationValue: undefined,
      expertModalShow: false,
      helpModalShow: false,
      commentModalShow: false,
      expertComment: undefined,
      commentExpertModalShow: false,
      page: 1,
      itemsPerPage: 5,
      zoom: 0,
      buttons: [
        {
          title: 'estimation.ordinary_money',
          action: 'ordinary_money',
          buttonClass: 'button button--danger button--list-danger',
        },
        {
          title: 'estimation.counterfeit_money',
          action: 'counterfeit_money',
          buttonClass: 'button button--danger button--list-danger',
        },
        {
          title: 'estimation.bullion_coin',
          action: 'bullion_coin',
          buttonClass: 'button button--danger button--list-danger',
        },
      ],
      options: {
        slidesPerView,
        spaceBetween: 30,
        pagination: {
          el: '.swiper-pagination',
          type: 'progressbar',
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
      },
    };
  },

  async created() {
    const filters = this.getFilters;

    filters.itemsPerPage = this.itemsPerPage;
    filters['createdAt[before]'] = Date.now();

    this.setFilters(filters);

    const estimations = await this.fetchEstimations(
      this.getFilters || { status: 'request' }
    );

    if (!estimations || !estimations['hydra:member']) return;

    this.estimations = estimations['hydra:member'];

    this.getCurrentEstimation();
  },

  mounted() {
    document.addEventListener('keydown', this.keyNav);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.keyNav);
  },

  computed: {
    ...mapGetters('app', ['getFilters', 'getReferer']),
    id() {
      return this.$route.params.id || false;
    },

    stringEstimationValue() {
      return (this.estimationValue && this.estimationValue.toString()) || '';
    },
  },

  methods: {
    ...mapActions('app', ['setToaster', 'setFilters']),

    async getCurrentEstimation() {
      if (!this.id) return;

      this.estimation = await this.fetchEstimation(this.id);
      this.activePicture = this.estimation.images[0].largeUrl;
    },

    setActive(index) {
      this.zoom = 0;
      this.activePicture = this.estimation.images[index].largeUrl;
    },

    getSelectedEstimation(id) {
      this.estimation = this.estimations[id];
      this.activePicture = this.estimation.images[0].largeUrl;
      this.zoom = 0;
      this.$router.push({
        name: 'estimation-full-view',
        params: { id: this.estimation.id },
      });
    },

    async estimationValidation() {
      try {
        const { estimationValue, estimation, expertComment } = this;
        const response = await this.submitEstimation({
          estimationValue,
          estimation,
          expertComment,
        });

        if (response.error !== undefined) {
          this.setToaster([
            {
              error: 'errors.estimation.positive_value',
              time: Date.now(),
            },
          ]);
          return;
        }

        await this.showNext();
        this.estimationValue = undefined;
      } catch (e) {
        this.setToaster([
          {
            error: 'errors.estimation.submit',
            time: Date.now(),
          },
        ]);

        this.getCurrentEstimation();
      }
    },

    async showNext() {
      if (this.estimations.length === 1) {
        this.$router.push({ name: 'pending-estimations' });
        return;
      }

      let currentIndex = this.estimations.findIndex(
        elem => elem.id === this.estimation.id
      );
      let nextId =
        currentIndex === this.estimations.length - 1
          ? this.estimations[0].id
          : this.estimations[currentIndex + 1].id;

      this.$router.push({
        name: 'estimation-full-view',
        params: { id: nextId },
      });

      this.estimations.splice(currentIndex, 1);

      const filters = this.getFilters;
      filters.page = this.page;
      this.setFilters(filters);

      const response = await this.fetchEstimations(
        this.getFilters || { status: 'request' }
      );

      if (!response || !response['hydra:member']) return;

      const estimations = response['hydra:member'];

      let newEstimations = [];

      estimations.forEach(estimation => {
        const existingEstimation = filter(this.estimations, { id: estimation.id });
        if (!existingEstimation.length) newEstimations.push(estimation);
      });

      this.estimations.push(...newEstimations);
      await this.getCurrentEstimation();
    },

    async rejectEstimation(rejectReason) {
      try {
        const expertComment = this.expertComment;
        await this.updateEstimation(this.estimation['@id'], {
          rejectReason,
          expertComment,
        });

        await this.showNext();
      } catch (e) {
        this.setToaster([
          {
            error: 'errors.estimation.reject',
            time: Date.now(),
          },
        ]);

        this.getCurrentEstimation();
      }
    },

    async submitAssignation(expert) {
      await this.updateAssignation(this.estimation['@id'], expert);
      await this.getCurrentEstimation();
      this.expertModalShow = false;
    },

    close() {
      this.$router.push(this.getReferer || { name: 'pending-estimations' });
    },

    async nextSlide() {
      const minItem = this.estimations.length - (this.navSwiper.activeIndex + 1);

      if (minItem <= slidesPerView) {
        this.page += 1;
        const { page, itemsPerPage } = this;
        const response = await this.paginateEstimations({ page, itemsPerPage });

        if (!response || !response['hydra:member']) return;

        this.estimations.push(response['hydra:member']);
      }
    },

    changeView() {
      if (this.zoom < 1) {
        const index = findIndex(
          this.estimation.images,
          img => img.largeUrl === this.activePicture
        );

        if (index > -1) {
          this.activePicture = this.estimation.images[index].originalUrl;
        }
      }

      this.zoom = this.zoom === 1 ? 0 : this.zoom + 1;
    },

    keyNav(e) {
      let stop = false;
      e = e || window.event;

      if (e.key === 'Right' || e.key === 'ArrowRight') {
        const nextSibling = document.querySelector('.swiper-slide--active')
          .nextElementSibling;
        if (nextSibling) nextSibling.click();
        document.querySelector('.swiper-button-next').click();
        stop = true;
      }
      if (e.key === 'Left' || e.key === 'ArrowLeft') {
        const previousSibling = document.querySelector('.swiper-slide--active')
          .previousElementSibling;
        if (previousSibling) previousSibling.click();
        document.querySelector('.swiper-button-prev').click();
        stop = true;
      }

      if (e.key === 'Up' || e.key === 'ArrowUp') {
        const img = document.querySelectorAll(
          '.full-view__aside__pictures__preview'
        )[0];
        if (img) img.click();
        stop = true;
      }
      if (e.key === 'Down' || e.key === 'ArrowDown') {
        const img = document.querySelectorAll(
          '.full-view__aside__pictures__preview'
        )[1];
        if (img) img.click();
        stop = true;
      }

      if (['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].indexOf(e.key) > -1) {
        this.estimationValue = parseInt(`${this.stringEstimationValue}${e.key}`, 10);
        stop = true;
      }

      if (e.key === 'Backspace' && this.stringEstimationValue) {
        this.estimationValue = this.stringEstimationValue.substring(
          0,
          this.stringEstimationValue.length - 1
        );
        stop = true;
      }

      if (e.key === 'Enter') {
        this.estimationValidation();
        stop = true;
      }

      if (e.key === 'i' && e.ctrlKey) {
        this.rejectEstimation(this.rejectReason.unusable_photo);
        stop = true;
      }

      if (e.key === 'f' && e.ctrlKey) {
        this.rejectEstimation(this.rejectReason.counterfeit_money);
        stop = true;
      }

      if (e.key === 'm' && e.ctrlKey) {
        this.rejectEstimation(this.rejectReason.ordinary_money);
        stop = true;
      }

      if (e.key === 'b' && e.ctrlKey) {
        this.rejectEstimation(this.rejectReason.bullion_coin);
        stop = true;
      }

      if (e.key === 'Esc' || e.key === 'Escape') {
        this.close();
        stop = true;
      }

      if (stop) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
  },
};
</script>
